import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Crown, Gamepad2, MessageCircle, Send, Shield, Star, Trophy, Trash2, User, UserCheck, UserPlus } from "lucide-react";
import { fetchPublicTeam } from "../../api/profileService";
import { requestJoinTeam, deleteTeam, fetchTeamMessages, sendTeamMessage } from "../../api/premiumSocialService";
import { useAuth } from "../../context/AuthContext";

function RoleIcon({ role }) {
  if (role === "chef") return <Crown className="h-4 w-4 text-amber-600" />;
  if (role === "co_chef") return <Shield className="h-4 w-4 text-sky-600" />;
  return <User className="h-4 w-4 text-slate-500" />;
}

export default function PublicTeam() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [score, setScore] = useState({ total: 0, games_played: 0 });
  const [categoryScores, setCategoryScores] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinFeedback, setJoinFeedback] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError("");
    fetchPublicTeam(teamId, controller.signal)
      .then((data) => {
        setTeam(data.team);
        setIsMember(Boolean(data.is_member));
        setHasPendingRequest(Boolean(data.has_pending_request));
        setScore(data.score || { total: 0, games_played: 0 });
        setCategoryScores(data.category_scores || []);
        setContributions(data.contributions || []);
      })
      .catch(() => setError("Equipe introuvable ou inaccessible."))
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, [teamId]);

  useEffect(() => {
    if (!team?.id || !isMember) return undefined;
    const controller = new AbortController();
    let interval;
    const load = () => {
      fetchTeamMessages(team.id, controller.signal)
        .then((data) => setChatMessages(data.messages || []))
        .catch(() => {});
    };
    load();
    interval = setInterval(load, 5000);
    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [team?.id, isMember]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || !team?.id || chatSending) return;
    setChatSending(true);
    sendTeamMessage(team.id, text)
      .then((data) => {
        if (data.message_sent) {
          setChatMessages((prev) => [...prev, data.message_sent]);
        }
        setChatInput("");
      })
      .catch(() => {})
      .finally(() => setChatSending(false));
  };

  const isOwner = team?.members?.some(
    (m) => m.user.id === currentUser?.id && m.role === "chef"
  );

  const memberIds = new Set((team?.members || []).map((m) => m.user.id));

  const handleDelete = () => {
    if (!team?.id || deleteLoading) return;
    if (!window.confirm("Supprimer definitivement cette equipe ? Cette action est irreversible.")) return;
    setDeleteLoading(true);
    deleteTeam(team.id)
      .then(() => navigate("/profile/premium-social"))
      .catch((err) => {
        setJoinFeedback(err?.response?.data?.error || "Impossible de supprimer l'equipe.");
        setDeleteLoading(false);
      });
  };

  const handleJoin = () => {
    if (!team?.id || joinLoading) return;
    setJoinLoading(true);
    setJoinFeedback("");
    requestJoinTeam(team.id)
      .then(() => {
        setHasPendingRequest(true);
        setJoinFeedback("Demande envoyee ! Le chef d'equipe doit l'accepter.");
      })
      .catch((err) => {
        setJoinFeedback(err?.response?.data?.error || "Erreur lors de la demande.");
      })
      .finally(() => setJoinLoading(false));
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      {isLoading ? (
        <div className="rounded-[32px] border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
          Chargement de l'equipe...
        </div>
      ) : error ? (
        <div className="rounded-[32px] border border-rose-200 bg-rose-50 p-8 text-sm text-rose-700 shadow-sm">
          {error}
        </div>
      ) : team ? (
        <div className="space-y-6">
          {/* Header */}
          <section
            className="relative rounded-[36px] border border-slate-200 bg-white p-5 shadow-sm"
            style={
              team.banner_status === "approved" && team.banner
                ? {
                    backgroundImage: `url(${team.banner})`,
                    backgroundSize: "cover",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }
                : undefined
            }
          >
            {team.banner_status === "approved" && team.banner && (
              <div className="banner-overlay" />
            )}
            <div className="relative z-10">
              <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
                <div className="flex flex-col gap-5 md:flex-row md:items-end">
                  <div className="shrink-0">
                    {team.logo ? (
                      <img src={team.logo} alt="" className="h-28 w-28 rounded-[28px] border-4 border-white object-cover shadow-xl" />
                    ) : (
                      <div className="flex h-28 w-28 items-center justify-center rounded-[28px] border-4 border-white bg-amber-500 text-2xl font-black text-white shadow-xl">
                        {team.tag}
                      </div>
                    )}
                  </div>
                  <div className="pb-2">
                    <h1 className="text-3xl font-black text-slate-950">{team.name} [{team.tag}]</h1>
                    <p className="mt-3 text-sm font-semibold text-black">{team.members.length} / 20 membre(s)</p>
                    {team.bio ? <p className="mt-4 text-sm text-black">{team.bio}</p> : null}
                  </div>
                </div>

                <div className="pb-2">
                  {isMember ? (
                    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700">
                      <UserCheck className="h-4 w-4" />
                      Tu es membre
                    </div>
                  ) : hasPendingRequest ? (
                    <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700">
                      <UserPlus className="h-4 w-4" />
                      Demande en attente
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleJoin}
                      disabled={joinLoading}
                      className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
                    >
                      <UserPlus className="h-4 w-4" />
                      {joinLoading ? "Envoi..." : "Demander a rejoindre"}
                    </button>
                  )}
                  {isOwner && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={deleteLoading || (team.members?.length ?? 0) > 1}
                      title={(team.members?.length ?? 0) > 1 ? "L'equipe doit etre vide pour etre supprimee" : "Supprimer l'equipe"}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {deleteLoading ? "Suppression..." : "Supprimer l'equipe"}
                    </button>
                  )}
                  {joinFeedback ? <p className="mt-2 text-xs text-slate-500">{joinFeedback}</p> : null}
                </div>
              </div>
            </div>
          </section>

          {/* Scores globaux */}
          <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="text-lg font-bold text-slate-950">Scores de l'équipe</div>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-5 py-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  <Star className="h-3.5 w-3.5" />
                  Score total
                </div>
                <div className="mt-2 text-2xl font-black text-slate-950">{score.total.toLocaleString()}</div>
                <div className="mt-0.5 text-[11px] font-semibold text-slate-400">pts</div>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-5 py-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  <Gamepad2 className="h-3.5 w-3.5" />
                  Parties jouees
                </div>
                <div className="mt-2 text-2xl font-black text-slate-950">{score.games_played}</div>
                <div className="mt-0.5 text-[11px] font-semibold text-slate-400">sessions</div>
              </div>
              <div className="col-span-2 flex items-center sm:col-span-1">
                <Link
                  to="/classement?tab=team_general"
                  className="inline-flex rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Voir le classement teams →
                </Link>
              </div>
            </div>

            {/* Scores par catégorie */}
            {categoryScores.length > 0 && (
              <div className="mt-6">
                <div className="mb-3 text-sm font-semibold text-slate-700">Par catégorie</div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {categoryScores.map((cat) => (
                    <div key={cat.category} className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                      <span className="truncate text-sm font-semibold text-slate-800">{cat.category_name}</span>
                      <span className="ml-2 shrink-0 text-sm font-black text-amber-700">{cat.score.toLocaleString()} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Contributions des joueurs */}
          {contributions.length > 0 && (
            <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-lg font-bold text-slate-950">
                <Trophy className="h-5 w-5 text-amber-500" />
                Contributions des joueurs
              </div>
              <p className="mt-1 text-xs text-slate-500">Points apportés à l'équipe par chaque joueur (anciens membres inclus).</p>
              <div className="mt-4 space-y-2">
                {contributions.map((contrib, i) => {
                  const isCurrentMember = memberIds.has(contrib.user_id);
                  return (
                    <div
                      key={contrib.user_id}
                      className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-black text-slate-600">
                          {i + 1}
                        </span>
                        {isCurrentMember ? (
                          <Link
                            to={`/joueur/u/${contrib.user_id}`}
                            className="truncate font-semibold text-slate-900 hover:text-sky-600 hover:underline"
                          >
                            {contrib.username}
                          </Link>
                        ) : (
                          <span className="truncate font-semibold text-slate-500">{contrib.username}</span>
                        )}
                        {!isCurrentMember && (
                          <span className="shrink-0 text-[10px] font-semibold text-slate-400">(ancien membre)</span>
                        )}
                      </div>
                      <span className="ml-2 shrink-0 text-sm font-black text-amber-700">
                        {contrib.total_points.toLocaleString()} pts
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Membres */}
          <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="text-lg font-bold text-slate-950">Membres</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {team.members.map((member) => (
                <Link
                  key={member.user.id}
                  to={`/joueur/u/${member.user.id}`}
                  className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 hover:bg-white"
                >
                  <span className="font-semibold text-slate-900">{member.user.username}</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-slate-600">
                    <RoleIcon role={member.role} />
                    {member.role}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Chat équipe */}
          {isMember && (
            <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-lg font-bold text-slate-950">
                <MessageCircle className="h-5 w-5 text-pixy-blue" />
                Discussion d'équipe
              </div>
              <div className="mt-4 flex h-72 flex-col gap-2 overflow-y-auto rounded-2xl border border-slate-100 bg-slate-50 p-4">
                {chatMessages.length === 0 ? (
                  <p className="text-sm text-slate-400">Aucun message pour le moment. Lance la discussion !</p>
                ) : (
                  chatMessages.map((msg) => (
                    <div key={msg.id} className="rounded-2xl bg-white px-3 py-2 shadow-sm">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs font-bold text-slate-800">{msg.author}</span>
                        <span className="text-[10px] text-slate-400">
                          {new Intl.DateTimeFormat("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          }).format(new Date(msg.created_at))}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-700">{msg.text}</p>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={handleSendMessage} className="mt-3 flex gap-2">
                <input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  maxLength={500}
                  placeholder="Écrire un message..."
                  className="h-11 flex-1 rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-pixy-blue"
                />
                <button
                  type="submit"
                  disabled={chatSending || !chatInput.trim()}
                  className="inline-flex h-11 items-center gap-2 rounded-2xl bg-pixy-ink px-4 text-sm font-bold text-white disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  Envoyer
                </button>
              </form>
            </section>
          )}
        </div>
      ) : null}
    </div>
  );
}
